import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FloatingFocusManager, type FloatingContext } from '@floating-ui/react';
import type { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import type React from 'react';
import classNames from '../../../../utils/classNames';
import type { ElementInsertionOption } from '../elements/types';

interface AddElementSlotMenuProps {
    isMenuOpen: boolean;
    context: FloatingContext;
    setFloatingRef: (node: HTMLDivElement | null) => void;
    floatingStyles: React.CSSProperties;
    floatingMenuProps: React.HTMLProps<HTMLDivElement>;
    insertionOptions: ElementInsertionOption[];
    activeMenuOptionIndex: number | null;
    onSelect: (option: ElementInsertionOption) => void;
    onMenuOptionMouseEnter: (optionIndex: number, isEnabled: boolean) => void;
    getMenuIconForElementType: (elementType: string) => IconDefinition;
}

export function AddElementSlotMenu({
    isMenuOpen,
    context,
    setFloatingRef,
    floatingStyles,
    floatingMenuProps,
    insertionOptions,
    activeMenuOptionIndex,
    onSelect,
    onMenuOptionMouseEnter,
    getMenuIconForElementType,
}: AddElementSlotMenuProps): JSX.Element | null {
    if (!isMenuOpen) {
        return null;
    }

    return (
        <FloatingFocusManager context={context} modal={false}>
            <div
                ref={setFloatingRef}
                style={floatingStyles}
                {...floatingMenuProps}
                className="z-20 w-64 rounded border border-slate-200 bg-white p-1 shadow-lg"
            >
                <ul>
                    {insertionOptions.map((option, optionIndex) => (
                        <li key={option.type}>
                            <button
                                type="button"
                                disabled={!option.isEnabled}
                                onClick={() => onSelect(option)}
                                onMouseEnter={() => {
                                    onMenuOptionMouseEnter(optionIndex, option.isEnabled);
                                }}
                                className={classNames(
                                    'w-full rounded px-3 py-2 text-left text-sm',
                                    activeMenuOptionIndex === optionIndex &&
                                        option.isEnabled &&
                                        'bg-slate-100',
                                    option.isEnabled
                                        ? 'text-slate-700 hover:bg-slate-100'
                                        : 'cursor-not-allowed text-slate-400'
                                )}
                            >
                                <span className="flex items-center justify-between gap-2">
                                    <span>{option.label}</span>
                                    <FontAwesomeIcon
                                        icon={getMenuIconForElementType(option.type)}
                                        className={
                                            option.isEnabled
                                                ? 'h-3.5 w-3.5 text-slate-400'
                                                : 'h-3.5 w-3.5 text-slate-300'
                                        }
                                        aria-hidden={true}
                                    />
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </FloatingFocusManager>
    );
}
